// src/components/drawer/PhraseConfigManager.tsx

import {
  Add as AddIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useAccount } from "../../../context";
import { usePhraseConfig } from "../../../hooks/usePhraseConfig";
import Header from "../../chat/Header/Header";

// Styled components seguindo exatamente o padrão do TemplatesSidebar
const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

const ScrollableContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const FooterContainer = styled(Box)`
  background-color: #f0f2f5;
  border-top: 1px solid #e9edef;
  padding: 13px;
  flex-shrink: 0;
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 16px 16px;
  margin-bottom: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 14px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PhraseChip = styled(Chip)`
  margin: 4px;
  background-color: #f0f2f5;
  color: #111b21;
  font-size: 12px;
  height: 28px;

  .MuiChip-deleteIcon {
    color: #667781;
    font-size: 16px;

    &:hover {
      color: #d32f2f;
    }
  }
`;

const InterestChip = styled(PhraseChip)`
  background-color: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
`;

const DisinterestChip = styled(PhraseChip)`
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
`;

const BlocklistChip = styled(PhraseChip)`
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
`;

const AddButton = styled(Button)`
  margin-top: 8px;
  text-transform: none;
  font-weight: 500;
  border-radius: 8px;
  border-color: #00a884;
  color: #00a884;

  &:hover {
    background-color: rgba(0, 168, 132, 0.1);
    border-color: #008f6f;
  }
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid #e9edef;
  margin-bottom: 20px;
  min-height: 52px;
  padding: 0;
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;

  .MuiTabs-indicator {
    background-color: #008069;
    height: 3px;
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .MuiTabs-flexContainer {
    flex-wrap: nowrap;
    position: relative;
    gap: 4px;
    align-items: center;
  }

  .MuiTabs-scroller {
    position: relative;
    overflow: hidden;
  }

  .MuiTabs-scrollButtons {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background-color: rgba(255, 255, 255, 0.95);
    color: #008069;
    border: 1px solid #e9edef;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 0 0 rgba(0, 168, 132, 0);
    margin: 0;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    align-self: center;
    position: relative;

    /* Indicador pulsante quando há mais conteúdo */
    &::before {
      content: "";
      position: absolute;
      inset: -2px;
      border-radius: 8px;
      background: linear-gradient(
        135deg,
        rgba(0, 168, 132, 0.1),
        rgba(0, 128, 105, 0.05)
      );
      opacity: 0;
      transition: opacity 0.3s ease;
      z-index: -1;
    }

    &:hover {
      background-color: #f8f9fa;
      color: #006d5b;
      border-color: #00a884;
      box-shadow:
        0 2px 6px rgba(0, 168, 132, 0.15),
        0 0 0 2px rgba(0, 168, 132, 0.1);
      backdrop-filter: blur(8px);
      transform: scale(1.05);

      &::before {
        opacity: 1;
      }
    }

    &:active {
      transform: scale(0.95);
    }

    &.Mui-disabled {
      opacity: 0.25;
      color: #adb5bd;
      background-color: rgba(255, 255, 255, 0.4);
      border-color: #e9edef;
      cursor: not-allowed;
    }

    /* Ajustar o ícone dentro do botão */
    .MuiSvgIcon-root {
      font-size: 16px;
      transition: transform 0.2s ease;
    }

    &:hover .MuiSvgIcon-root {
      transform: scale(1.1);
    }
  }

  /* Container dos botões de scroll para centralização */
  .MuiTabs-scrollButtonsContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 0px;
    padding: 0;
    position: relative;
    background: transparent;
  }

  .MuiTab-root {
    text-transform: none;
    font-weight: 500;
    font-size: 13px;
    color: #667781;
    padding: 14px 18px;
    min-height: 52px;
    white-space: nowrap;
    min-width: auto;
    flex-shrink: 0;
    border-radius: 8px 8px 0 0;
    margin-right: 2px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: visible;

    /* Efeito de brilho sutil no hover */
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 8px 8px 0 0;
      background: linear-gradient(135deg, rgba(0, 168, 132, 0.05), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    /* Melhorar o espaçamento entre ícone e texto */
    .MuiTab-iconWrapper {
      margin-right: 6px;
      margin-bottom: 1px;
      font-size: 15px;
      transition:
        transform 0.2s ease,
        color 0.2s ease;
    }

    &.Mui-selected {
      color: #008069;
      font-weight: 600;
      background-color: rgba(0, 128, 105, 0.06);
      transform: translateY(-1px);

      &::before {
        opacity: 1;
      }

      &::after {
        content: "";
        position: absolute;
        bottom: -1px;
        left: 8px;
        right: 8px;
        height: 3px;
        background: linear-gradient(90deg, transparent, #008069, transparent);
        border-radius: 2px;
        animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .MuiTab-iconWrapper {
        transform: scale(1.1);
        color: #008069;
      }
    }

    &:hover:not(.Mui-selected) {
      color: #008069;
      background-color: rgba(0, 128, 105, 0.08);
      transform: translateY(-1px);

      &::before {
        opacity: 0.5;
      }

      .MuiTab-iconWrapper {
        transform: scale(1.05);
      }
    }

    /* Priorizar as 2 primeiras abas */
    &:nth-of-type(1),
    &:nth-of-type(2) {
      flex-shrink: 0;
      min-width: fit-content;
    }

    /* Indicador de "mais conteúdo" para abas não visíveis */
    &:not(:first-of-type):not(:nth-of-type(2)) {
      position: relative;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scaleX(0);
    }
    to {
      opacity: 1;
      transform: scaleX(1);
    }
  }
`;

const TabPanel = styled(Box)<{ hidden: boolean }>`
  display: ${(props) => (props.hidden ? "none" : "block")};
  animation: ${(props) => (props.hidden ? "fadeOut" : "fadeIn")} 0.3s
    ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`;

const PhraseConfigManager: React.FC = memo(() => {
  const {
    phraseConfig,
    loading,
    saving,
    loadPhraseConfig,
    addPhrase,
    removePhrase,
    saveChanges,
  } = usePhraseConfig();

  const { account } = useAccount();
  const [activeTab, setActiveTab] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPhraseType, setNewPhraseType] = useState<
    "interest" | "disinterest" | "blocklist"
  >("interest");
  const [newPhrase, setNewPhrase] = useState("");

  useEffect(() => {
    loadPhraseConfig();
  }, [loadPhraseConfig]);

  const handleAddPhrase = useCallback(() => {
    addPhrase(newPhraseType, newPhrase);
    setNewPhrase("");
    setShowAddModal(false);
  }, [addPhrase, newPhraseType, newPhrase]);

  const handleSave = useCallback(async () => {
    await saveChanges();
  }, [saveChanges]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleAddPhrase();
      }
    },
    [handleAddPhrase],
  );

  const openAddModal = useCallback(
    (type: "interest" | "disinterest" | "blocklist") => {
      setNewPhraseType(type);
      setNewPhrase("");
      setShowAddModal(true);
    },
    [],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    [],
  );

  const isAdministrator = account?.role?.toUpperCase() === "ADMINISTRATOR";

  return (
    <Container>
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Botões Iterativos" />
      </Box>

      <ContentContainer>
        {loading ? (
          <>
            {/* Skeleton das tabs */}
            <Box
              sx={{
                px: 2,
                pt: 1,
                pb: 1,
                borderBottom: "1px solid #e9edef",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={36}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rectangular"
                  width={140}
                  height={36}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={36}
                  sx={{ borderRadius: "8px" }}
                />
              </Box>
            </Box>

            <ScrollableContent>
              {/* Skeleton do conteúdo (chips + botões) */}
              <Box sx={{ p: 2 }}>
                <BoxWrapper>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Skeleton variant="circular" width={18} height={18} />
                    <Skeleton variant="text" width="40%" height={18} />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                  >
                    {Array.from({ length: 10 }).map((_, idx) => (
                      <Skeleton
                        key={`phrase-chip-skeleton-${idx}`}
                        variant="rounded"
                        width={90 + (idx % 3) * 20}
                        height={28}
                        sx={{ borderRadius: "16px" }}
                      />
                    ))}
                  </Box>

                  <Skeleton
                    variant="rectangular"
                    width={140}
                    height={34}
                    sx={{ borderRadius: "8px" }}
                  />
                </BoxWrapper>
              </Box>
            </ScrollableContent>

            {/* Skeleton do footer (botão salvar) */}
            <FooterContainer>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ borderRadius: "8px" }}
              />
            </FooterContainer>
          </>
        ) : (
          <>
            <ScrollableContent>
              {/* Tabs para separar Interesse, Desinteresse e Blocklist */}
              <Box
                sx={{
                  px: 2,
                  pt: 1,
                  pb: 1,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "1px",
                    background:
                      "linear-gradient(to right, transparent, #e9edef, transparent)",
                  },
                }}
              >
                <StyledTabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons={true}
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTabs-scrollButtons": {
                      display: "flex !important",
                      alignSelf: "center !important",
                    },
                    "& .MuiTabs-scrollButtonsContainer": {
                      display: "flex !important",
                      alignItems: "center !important",
                      height: "100% !important",
                    },
                  }}
                >
                  <Tab
                    label={`Interesse (${phraseConfig.interest.length})`}
                    icon={<ThumbUpIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label={`Desinteresse (${phraseConfig.disinterest.length})`}
                    icon={<ThumbDownIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label={`Blocklist (${phraseConfig.blocklist.length})`}
                    icon={<BlockIcon />}
                    iconPosition="start"
                  />
                </StyledTabs>
              </Box>

              {/* Tab Panel - Interesse */}
              <TabPanel hidden={activeTab !== 0}>
                <BoxWrapper>
                  <SectionTitle>
                    👍 Frases de Interesse ({phraseConfig.interest.length})
                  </SectionTitle>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: "13px" }}
                  >
                    Frases que correspondem aos botões de interesse nas
                    mensagens iterativas.
                  </Typography>

                  {/* Lista de frases de interesse */}
                  <Box sx={{ mb: 2, minHeight: "60px" }}>
                    {phraseConfig.interest.map((phrase, index) => (
                      <InterestChip
                        key={index}
                        label={phrase}
                        onDelete={() => removePhrase("interest", index)}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>

                  {/* Botão para adicionar nova frase */}
                  <AddButton
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => openAddModal("interest")}
                    fullWidth
                  >
                    Adicionar Mapeamento de Interesse
                  </AddButton>
                </BoxWrapper>
              </TabPanel>

              {/* Tab Panel - Desinteresse */}
              <TabPanel hidden={activeTab !== 1}>
                <BoxWrapper>
                  <SectionTitle>
                    👎 Frases de Desinteresse ({phraseConfig.disinterest.length}
                    )
                  </SectionTitle>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: "13px" }}
                  >
                    Frases que correspondem aos botões de desinteresse nas
                    mensagens iterativas.
                  </Typography>

                  {/* Lista de frases de desinteresse */}
                  <Box sx={{ mb: 2, minHeight: "60px" }}>
                    {phraseConfig.disinterest.map((phrase, index) => (
                      <DisinterestChip
                        key={index}
                        label={phrase}
                        onDelete={() => removePhrase("disinterest", index)}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>

                  {/* Botão para adicionar nova frase */}
                  <AddButton
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => openAddModal("disinterest")}
                    fullWidth
                  >
                    Adicionar Mapeamento de Desinteresse
                  </AddButton>
                </BoxWrapper>
              </TabPanel>

              {/* Tab Panel - Blocklist */}
              <TabPanel hidden={activeTab !== 2}>
                <BoxWrapper>
                  <SectionTitle>
                    🚫 Frases de Blocklist ({phraseConfig.blocklist.length})
                  </SectionTitle>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: "13px" }}
                  >
                    Frases que correspondem aos botões de bloqueio nas mensagens
                    iterativas.
                  </Typography>

                  {/* Lista de frases de blocklist */}
                  <Box sx={{ mb: 2, minHeight: "60px" }}>
                    {phraseConfig.blocklist.map((phrase, index) => (
                      <BlocklistChip
                        key={index}
                        label={phrase}
                        onDelete={() => removePhrase("blocklist", index)}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>

                  {/* Botão para adicionar nova frase */}
                  <AddButton
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => openAddModal("blocklist")}
                    fullWidth
                  >
                    Adicionar Mapeamento de Blocklist
                  </AddButton>
                </BoxWrapper>
              </TabPanel>

              {/* Informações adicionais */}
              <BoxWrapper>
                <SectionTitle>💡 Informações</SectionTitle>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "13px" }}
                >
                  Estas frases são usadas para mapear os botões iterativos das
                  mensagens. Quando um cliente clica em um botão, o sistema
                  compara o texto do botão com estas frases para determinar a
                  ação.
                </Typography>
              </BoxWrapper>
            </ScrollableContent>
          </>
        )}
      </ContentContainer>

      {/* Botão fixo no rodapé - apenas para ADMINISTRATOR */}
      {isAdministrator && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
            minHeight: "64px",
            maxHeight: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            startIcon={saving ? null : <SaveIcon />}
            onClick={handleSave}
            fullWidth
            sx={{
              backgroundColor: "#008069",
              "&:hover": {
                backgroundColor: "#006d5a",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
              },
              fontSize: "14px",
              padding: "8px",
              textTransform: "none",
              fontWeight: 500,
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {saving ? "Salvando…" : "Salvar Mapeamento"}
            </Typography>
          </Button>
        </Box>
      )}

      {/* Modal para adicionar nova frase */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              minWidth: "400px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
            color: "#111b21",
            fontWeight: 600,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {newPhraseType === "interest" ? (
              <ThumbUpIcon color="success" />
            ) : newPhraseType === "disinterest" ? (
              <ThumbDownIcon color="error" />
            ) : (
              <BlockIcon color="warning" />
            )}
            <Typography variant="h6">
              Adicionar Mapeamento de{" "}
              {newPhraseType === "interest"
                ? "Interesse"
                : newPhraseType === "disinterest"
                  ? "Desinteresse"
                  : "Blocklist"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Nova frase"
            placeholder={`Digite o texto do botão de ${
              newPhraseType === "interest"
                ? "interesse"
                : newPhraseType === "disinterest"
                  ? "desinteresse"
                  : "blocklist"
            }...`}
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setShowAddModal(false)}
            sx={{
              color: "#667781",
              "&:hover": { backgroundColor: "#f0f2f5" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPhrase}
            disabled={!newPhrase.trim()}
            sx={{
              backgroundColor: "#008069",
              "&:hover": { backgroundColor: "#006d5b" },
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

PhraseConfigManager.displayName = "PhraseConfigManager";

export default PhraseConfigManager;
